if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.initDeferredVideos();
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
    }

    // Gallery videos after the first slide are rendered with preload="none" and no autoplay (sections/main-product.liquid),
    // so they don't download on page load. Show the poster as the slide approaches, and start playback (muted, looping)
    // the first time the slide is actually in view, which is what autoplay used to do.
    initDeferredVideos() {
      const videos = this.querySelectorAll('video.slide__video--deferred');
      if (!videos.length) return;
      const setPoster = (video) => {
        const poster = video.closest('[data-video-poster]')?.dataset.videoPoster;
        if (poster && !video.poster) video.poster = poster;
      };
      const start = (video) => {
        setPoster(video);
        video.preload = 'auto';
        video.play().catch(() => {});
      };
      if (!('IntersectionObserver' in window)) {
        videos.forEach(start);
        return;
      }
      const nearby = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          setPoster(entry.target);
          nearby.unobserve(entry.target);
        });
      }, { rootMargin: '100%' });
      const inView = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          start(entry.target);
          inView.unobserve(entry.target);
        });
      }, { threshold: 0.25 });
      videos.forEach((video) => {
        nearby.observe(video);
        inView.observe(video);
      });
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer?.querySelector(`[data-media-id="${mediaId}"]`);
        // Early return if activeMedia is not found
        if (!activeMedia) return;
    
        // Safely remove 'is-active' class from all elements
        this.elements.viewer?.querySelectorAll('[data-media-id]')?.forEach((element) => {
            element.classList.remove('is-active');
        });
        
        activeMedia.classList.add('is-active');
    
        if (prepend) {
            // Check if activeMedia and its parentElement exist before prepending
            if (activeMedia && activeMedia.parentElement) {
                activeMedia.parentElement.prepend(activeMedia);
            }
    
            // Check thumbnails related elements
            if (this.elements.thumbnails) {
                const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
                // Check if activeThumbnail and its parentElement exist before prepending
                if (activeThumbnail && activeThumbnail.parentElement) {
                    activeThumbnail.parentElement.prepend(activeThumbnail);
                }
            }
    
            // Check if viewer and slider exist before resetting pages
            if (this.elements.viewer?.slider) {
                this.elements.viewer.resetPages();
            }
        }
    
        this.preventStickyHeader();
        
        window.setTimeout(() => {
            if (this.elements.thumbnails && activeMedia && activeMedia.parentElement) {
                activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
            }
            if ((!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') && activeMedia) {
                activeMedia.scrollIntoView({behavior: 'smooth'});
            }
        });
    
        if (activeMedia) {
            this.playActiveMedia(activeMedia);
        }
    
        if (!this.elements.thumbnails) return;
        
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
    
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
